<template>
  <li class="CartItem">
    <template v-if="props.item.is_bundle">
      <div class="CartItem__Upper">
        <component
          :is="conditionalElement"
          v-bind="conditionalProps(props.item.url)"
          class="t4 CartItem__Title"
        >
          {{ props.item.title }}
        </component>
        <Price
          :item="props.item"
          :ready="ready"
          :variant="props.item"
          :location="location"
          :cart="props.items"
          :components="props?.item?.components"
        />
      </div>
      <ul v-if="hasComponents" class="CartItem__Lower">
        <li
          v-for="component in props.item.components"
          :key="component"
          class="CartItem CartItem--Inner"
        >
          <LineItemMedia :item="component" />
          <div class="CartItem__Details">
            <component
              :is="conditionalElement"
              v-bind="conditionalProps(component.url)"
              class="t4 CartItem__Title"
            >
              {{ component.title }}
            </component>
            <LineItemOptions :options="component.options" />
            <Price
              :item="component"
              :isComponent="true"
              :ready="ready"
              :variant="props.item"
              :location="location"
              :cart="props?.items"
            />
            <LineItemProperties :item="component" />
            <Backorder
              v-if="component.properties.Ships"
              :date="component.properties.Ships"
            />
            <p class="p5" v-if="componentDiscountNotAllowed(component)">
              {{ exclusionMessaging }}
            </p>
          </div>
        </li>
      </ul>
      <LineItemActions
        @handle-change="handleChange"
        :quantity="props.item.quantity"
        :disabled="props.item.disable_qty"
        :quantityAvailable="leastAvailableComponent"
      />
    </template>
    <template v-else>
      <LineItemMedia :item="props.item" />
      <div class="CartItem__Details">
        <component
          :is="conditionalElement"
          v-bind="conditionalProps(props.item.url)"
          class="t4 CartItem__Title"
        >
          {{ props.item.title }}
        </component>
        <LineItemOptions :options="props.item.options" />
        <Price
          :item="item"
          :ready="ready"
          :location="location"
          :variant="props.item"
          :cart="props.items"
        />
        <LineItemProperties :item="props.item" />
        <Backorder
          v-if="props.item.properties.Ships"
          :date="props.item.properties.Ships"
        />
        <p class="d2" v-if="ineligibleForDiscount" data-color="night-sky-dark">
          {{ exclusionMessaging }}
        </p>
      </div>
      <LineItemActions
        @handle-change="handleChange"
        :quantity="props.item.quantity"
        :disabled="props.item.disable_qty"
        :quantityAvailable="props.item.quantityAvailable"
        :is-gift-card="isGiftCard"
      />
    </template>
  </li>
</template>

<script setup>
import { computed, watch } from 'vue';
import { useCartStore } from './Composables/Store';
import Backorder from '../Product/Backorder.vue';
import LineItemMedia from './LineItemMedia.vue';
import LineItemOptions from './LineItemOptions.vue';
import LineItemActions from './LineItemActions.vue';
import LineItemProperties from './LineItemProperties.vue';
import Price from '../Global/Price.vue';

const store = useCartStore();
const props = defineProps({
  item: Object,
  items: Array,
  ready: { type: Boolean, default: false },
});

const isGiftCard = computed(() => {
  return props.item.vendor == 'Rise.ai' ? true : false;
});

const location = computed(() => 'cart');
const leastAvailableComponent = computed(() => {
  const components = props.item.components;
  const quantities = components.map((component) => component.quantityAvailable);
  return Math.min(...quantities);
});

const exclusionMessaging = computed(() => {
  const codeExclusion = store.terms?.discount_ineligible;
  const swdExclusion = store.terms?.swd_exclusion_msg;
  return props.item?.discount_messaging ? '' : codeExclusion;
});
const createEvent = (items) =>
  new CustomEvent('theme:add-to-cart', {
    detail: items,
  });

const handleChange = async (quantity) => {
  const item = props?.item ?? {};
  const line = store.getLineInCart(item);
  const lineItem = store.$state.items[line];
  const isGiftNote = lineItem.variant_id === store.$state.gift_note;
  const isGiftWithPurchase = store.isGiftWithPurcase(item);

  if (isGiftNote) await store.addGiftNote();

  const payload = {};
  const mode = store.$state.mode;
  const global = store.$state.global;
  const tote = global?.tote ?? false;

  if (tote) {
    const isTote = lineItem.variant_id === global.tote.env[mode].variant;
    if (isTote && quantity === 0) store.handleToteInteraction();
  }

  if (isGiftWithPurchase && quantity === 0) {
    store.removeGWP();
    store.displayGWP();
    return;
  }

  payload.id = lineItem.key;
  payload.quantity = quantity;

  const event = createEvent(payload);
  return document.dispatchEvent(event);
};

const discountMessagingPermitted = computed(() => {
  return props?.item?.discount_messaging;
});

const componentDiscountNotAllowed = computed(() => {
  return (item) => {
    const noMessaging = !item?.discount_messaging;
    const ineligible = !item?.discount_eligible;
    const notGiftNote = item?.variant_id !== store.$state.gift_note;
    return ineligible && noMessaging && notGiftNote;
  };
});

const ineligibleForDiscount = computed(() => {
  const noMessaging = !discountMessagingPermitted.value;
  const ineligible = !props.item.discount_eligible;
  const notGiftNote = props.item.variant_id !== store.$state.gift_note;
  return ineligible && noMessaging && notGiftNote;
});

const handleNote = () => {
  const noteInCart = store.hasNote;
  if (!noteInCart) return;

  const items = store.getLineItems;
  const ids = items.map((item) => item.variant_id);
  const hasNoteItem = ids.includes(store.$state.gift_note);
  const onlyGiftRemaining = hasNoteItem && items.length === 1;

  if (!onlyGiftRemaining) return;

  store.addGiftNote();
  return handleChange(0);
};

const conditionalElement = computed(() => {
  const ineligibleItems = store.getIneligibleItems;
  const variant = props.item.variant_id;
  const isIneligible = ineligibleItems.includes(variant);
  return isIneligible ? 'div' : 'a';
});

const conditionalProps = computed(() => {
  return (url) => {
    const isLink = conditionalElement.value === 'a';
    if (!isLink) return {};
    return {
      href: url,
    };
  };
});

const hasComponents = computed(() => props.item.components.length > 0);

watch(() => store.$state.items, handleNote, { immediate: true });
</script>
